import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import StudentLayout from '@/components/layouts/StudentLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { 
  Brain, Shapes, Calculator, BookOpen, ClipboardCheck,
  ChevronRight, ArrowLeft, Clock, Target, Trophy, Star,
  CheckCircle, AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';

const iconMap = {
  Brain: Brain,
  Shapes: Shapes,
  Calculator: Calculator,
  BookOpen: BookOpen,
  ClipboardCheck: ClipboardCheck
};

const ElevenPlusPage = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [topics, setTopics] = useState([]);
  const [papers, setPapers] = useState([]);
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [lessons, setLessons] = useState({});
  const [currentLesson, setCurrentLesson] = useState(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [score, setScore] = useState(0);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      // Fetch topics and papers first (available to any logged-in user)
      const [topicsRes, papersRes] = await Promise.all([
        api.get('/v2/eleven-plus/topics'),
        api.get('/v2/eleven-plus/practice-papers')
      ]);
      setTopics(topicsRes.data.topics || []);
      setPapers(papersRes.data.papers || []);
      
      // Try to fetch progress (student-only endpoint)
      try {
        const progressRes = await api.get('/v2/eleven-plus/progress');
        setProgress(progressRes.data);
      } catch (progressError) {
        // Progress endpoint may fail for non-students (admin preview mode)
        console.log('Progress not available (admin preview mode)');
        setProgress(null);
      }
    } catch (error) {
      console.error('Error fetching 11+ data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchLessons = async (topicId) => {
    try {
      const res = await api.get(`/v2/eleven-plus/lessons/${topicId}`);
      setLessons(res.data.lessons || {});
      setSelectedTopic(topicId);
    } catch (error) {
      toast.error('Failed to load lessons');
    }
  };

  const startLesson = (sectionId, lessonData) => {
    setCurrentLesson({ sectionId, ...lessonData });
    setCurrentQuestionIndex(0);
    setSelectedAnswer(null);
    setShowResult(false);
    setScore(0);
  };

  const handleAnswer = (answerIndex) => {
    if (showResult) return;
    setSelectedAnswer(answerIndex);
  };

  const submitAnswer = () => {
    if (selectedAnswer === null) return;
    
    const exercise = currentLesson.exercises[currentQuestionIndex];
    const isCorrect = selectedAnswer === exercise.correct_answer;
    
    if (isCorrect) {
      setScore(prev => prev + 1);
    }
    setShowResult(true);
  };

  const nextQuestion = () => {
    if (currentQuestionIndex < currentLesson.exercises.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
      setSelectedAnswer(null);
      setShowResult(false);
    } else {
      // Lesson complete
      const percentage = Math.round((score / currentLesson.exercises.length) * 100);
      toast.success(`Lesson complete! Score: ${percentage}%`);
      setCurrentLesson(null);
      fetchData(); // Refresh progress
    }
  };

  if (loading) {
    return (
      <StudentLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-600 border-t-transparent"></div>
        </div>
      </StudentLayout>
    );
  }

  // Lesson view
  if (currentLesson) {
    const exercise = currentLesson.exercises[currentQuestionIndex];
    const progressPercent = ((currentQuestionIndex + 1) / currentLesson.exercises.length) * 100;

    return (
      <StudentLayout>
        <div className="max-w-3xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <button 
              onClick={() => setCurrentLesson(null)}
              className="flex items-center gap-2 text-slate-600 hover:text-slate-800"
            >
              <ArrowLeft className="w-5 h-5" />
              Exit Lesson
            </button>
            <Badge variant="outline" className="text-purple-600">
              Question {currentQuestionIndex + 1} of {currentLesson.exercises.length}
            </Badge>
          </div>

          {/* Progress */}
          <Progress value={progressPercent} className="mb-6 h-2" />

          {/* Explanation Card (first question only) */}
          {currentQuestionIndex === 0 && currentLesson.explanation && (
            <Card className="mb-6 bg-purple-50 border-purple-200">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg text-purple-800">
                  {currentLesson.explanation.title}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-700 mb-2">{currentLesson.explanation.content}</p>
                <p className="text-sm text-purple-700 italic">
                  💡 Tip: {currentLesson.explanation.tip}
                </p>
              </CardContent>
            </Card>
          )}

          {/* Question Card */}
          <Card className="mb-6">
            <CardContent className="pt-6">
              <h3 className="text-lg font-semibold mb-6">{exercise.question}</h3>

              <div className="grid gap-3">
                {exercise.options.map((option, idx) => {
                  let buttonClass = "w-full text-left p-4 rounded-lg border-2 transition-all ";
                  
                  if (showResult) {
                    if (idx === exercise.correct_answer) {
                      buttonClass += "border-green-500 bg-green-50 text-green-800";
                    } else if (idx === selectedAnswer && idx !== exercise.correct_answer) {
                      buttonClass += "border-red-500 bg-red-50 text-red-800";
                    } else {
                      buttonClass += "border-slate-200 text-slate-500";
                    }
                  } else {
                    if (idx === selectedAnswer) {
                      buttonClass += "border-purple-500 bg-purple-50";
                    } else {
                      buttonClass += "border-slate-200 hover:border-purple-300";
                    }
                  }

                  return (
                    <button
                      key={idx}
                      onClick={() => handleAnswer(idx)}
                      className={buttonClass}
                      disabled={showResult}
                      data-testid={`answer-option-${idx}`}
                    >
                      <span className="font-medium mr-2">{String.fromCharCode(65 + idx)}.</span>
                      {option}
                      {showResult && idx === exercise.correct_answer && (
                        <CheckCircle className="w-5 h-5 inline ml-2 text-green-600" />
                      )}
                      {showResult && idx === selectedAnswer && idx !== exercise.correct_answer && (
                        <AlertCircle className="w-5 h-5 inline ml-2 text-red-600" />
                      )}
                    </button>
                  );
                })}
              </div>

              {/* Explanation after answer */}
              {showResult && (
                <div className={`mt-4 p-4 rounded-lg ${
                  selectedAnswer === exercise.correct_answer ? 'bg-green-50' : 'bg-amber-50'
                }`}>
                  <p className="text-sm">
                    <strong>Explanation:</strong> {exercise.explanation}
                  </p>
                </div>
              )}

              {/* Action buttons */}
              <div className="mt-6 flex justify-end gap-3">
                {!showResult ? (
                  <Button 
                    onClick={submitAnswer}
                    disabled={selectedAnswer === null}
                    className="bg-purple-600 hover:bg-purple-700"
                    data-testid="submit-answer-btn"
                  >
                    Submit Answer
                  </Button>
                ) : (
                  <Button 
                    onClick={nextQuestion}
                    className="bg-purple-600 hover:bg-purple-700"
                    data-testid="next-question-btn"
                  >
                    {currentQuestionIndex < currentLesson.exercises.length - 1 ? 'Next Question' : 'Finish Lesson'}
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Score */}
          <div className="text-center text-slate-600">
            Current Score: {score}/{currentQuestionIndex + (showResult ? 1 : 0)}
          </div>
        </div>
      </StudentLayout>
    );
  }

  // Topic lessons view
  if (selectedTopic) {
    const topic = topics.find(t => t.id === selectedTopic);
    const Icon = iconMap[topic?.icon] || Brain;

    return (
      <StudentLayout>
        <div className="mb-6">
          <button 
            onClick={() => setSelectedTopic(null)}
            className="flex items-center gap-2 text-slate-600 hover:text-slate-800 mb-4"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Topics
          </button>
          
          <div className="flex items-center gap-4">
            <div 
              className="w-14 h-14 rounded-xl flex items-center justify-center"
              style={{ backgroundColor: topic?.color + '20' }}
            >
              <Icon className="w-7 h-7" style={{ color: topic?.color }} />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-800">{topic?.name}</h1>
              <p className="text-slate-600">{topic?.description}</p>
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          {Object.entries(lessons).map(([sectionId, lesson]) => (
            <Card key={sectionId} className="hover:shadow-lg transition-shadow cursor-pointer"
                  onClick={() => startLesson(sectionId, lesson)}>
              <CardContent className="pt-6">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-bold text-slate-800 mb-1">{lesson.title}</h3>
                    <p className="text-sm text-slate-600 mb-3">
                      {lesson.exercises?.length || 0} questions
                    </p>
                    <Badge variant="outline">{sectionId.replace('_', ' ')}</Badge>
                  </div>
                  <ChevronRight className="w-5 h-5 text-slate-400" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </StudentLayout>
    );
  }

  // Main view - Topics grid
  return (
    <StudentLayout>
      <div className="mb-8">
        <Link to="/student" className="flex items-center gap-2 text-slate-600 hover:text-slate-800 mb-4">
          <ArrowLeft className="w-5 h-5" />
          Back to Dashboard
        </Link>
        
        <div className="bg-gradient-to-r from-purple-600 to-pink-600 rounded-2xl p-6 text-white">
          <div className="flex items-center gap-3 mb-2">
            <Trophy className="w-8 h-8" />
            <h1 className="text-2xl font-bold">11+ Exam Preparation</h1>
          </div>
          <p className="text-white/80">
            Master Verbal Reasoning, Non-Verbal Reasoning, Maths, and English
          </p>
        </div>
      </div>

      {/* Progress Stats */}
      {progress && progress.total_papers_completed > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card>
            <CardContent className="pt-4 text-center">
              <div className="text-2xl font-bold text-purple-600">{progress.total_papers_completed}</div>
              <div className="text-sm text-slate-600">Papers Done</div>
            </CardContent>
          </Card>
          {Object.entries(progress.stats_by_type || {}).map(([type, stats]) => (
            stats.best_score > 0 && (
              <Card key={type}>
                <CardContent className="pt-4 text-center">
                  <div className="text-2xl font-bold text-purple-600">{stats.best_score}%</div>
                  <div className="text-sm text-slate-600 capitalize">{type} Best</div>
                </CardContent>
              </Card>
            )
          ))}
        </div>
      )}

      {/* Topics Grid */}
      <h2 className="text-xl font-bold text-slate-800 mb-4">Study Topics</h2>
      <div className="grid md:grid-cols-2 gap-4 mb-8">
        {topics.map(topic => {
          const Icon = iconMap[topic.icon] || Brain;
          return (
            <Card 
              key={topic.id} 
              className="hover:shadow-lg transition-shadow cursor-pointer border-2 hover:border-purple-300"
              onClick={() => fetchLessons(topic.id)}
              data-testid={`topic-${topic.id}`}
            >
              <CardContent className="pt-6">
                <div className="flex items-start gap-4">
                  <div 
                    className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: topic.color + '20' }}
                  >
                    <Icon className="w-6 h-6" style={{ color: topic.color }} />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-slate-800 mb-1">{topic.name}</h3>
                    <p className="text-sm text-slate-600 mb-2">{topic.description}</p>
                    <div className="flex flex-wrap gap-1">
                      {topic.sections?.slice(0, 3).map(section => (
                        <Badge key={section} variant="outline" className="text-xs">
                          {section.replace('_', ' ')}
                        </Badge>
                      ))}
                      {topic.sections?.length > 3 && (
                        <Badge variant="outline" className="text-xs">
                          +{topic.sections.length - 3} more
                        </Badge>
                      )}
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-slate-400" />
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Practice Papers */}
      <h2 className="text-xl font-bold text-slate-800 mb-4">Practice Papers</h2>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {papers.map(paper => (
          <Card key={paper.id} className="hover:shadow-lg transition-shadow">
            <CardContent className="pt-6">
              <div className="flex items-center gap-2 mb-2">
                <ClipboardCheck className="w-5 h-5 text-purple-600" />
                <Badge variant="secondary" className="capitalize">
                  {paper.type}
                </Badge>
              </div>
              <h3 className="font-bold text-slate-800 mb-2">{paper.name}</h3>
              <div className="flex items-center gap-4 text-sm text-slate-600 mb-4">
                <span className="flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  {paper.duration} min
                </span>
                <span className="flex items-center gap-1">
                  <Target className="w-4 h-4" />
                  {paper.questions} questions
                </span>
              </div>
              <Badge variant={paper.difficulty === 'challenging' ? 'destructive' : 'outline'}>
                {paper.difficulty}
              </Badge>
            </CardContent>
          </Card>
        ))}
      </div>
    </StudentLayout>
  );
};

export default ElevenPlusPage;
